import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, CalendarClock, Award, Download, Trash2, Plus, UserCheck, Pencil, X as XIcon } from 'lucide-react';

const STATUS_META = {
  assigned: { label: 'Assigned', cls: 'bg-[#F0EDF6] text-[#5A4D6E]' },
  not_started: { label: 'Assigned', cls: 'bg-[#F0EDF6] text-[#5A4D6E]' },
  in_progress: { label: 'In progress', cls: 'bg-[#E3F2FD] text-[#0D47A1]' },
  submitted: { label: 'Awaiting review', cls: 'bg-[#FFF3E0] text-[#B45309]' },
  approved: { label: 'Completed', cls: 'bg-[#E8F5E9] text-[#1B5E20]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FFEBEE] text-[#B71C1C]' },
};

const ISP_PROGRAMS = ['CPS', 'Employment', 'IHCS'];

export default function AnnualTrainingTab({ api }) {
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);          // active staff (assign card)
  const [formerStaff, setFormerStaff] = useState([]);   // archived/inactive (ISP log)
  const [participants, setParticipants] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [isp, setIsp] = useState([]);
  const [loading, setLoading] = useState(true);
  // assign form
  const [moduleId, setModuleId] = useState('');
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [resetProgress, setResetProgress] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  // ISP form
  const [ispForm, setIspForm] = useState({ employee_ids: [], date: '', hours: '', participant: '', participantOther: '', notes: '', trainer_name: '', program: '' });
  const [ispSaving, setIspSaving] = useState(false);
  const [ispSearch, setIspSearch] = useState('');
  const [editingIsp, setEditingIsp] = useState(null);   // record being corrected

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, emp, ispRes, parts, trs] = await Promise.all([
        api.get('/onboarding/annual-overview'),
        api.get('/employees'),
        api.get('/onboarding/isp-trainings'),
        api.get('/participants').catch(() => ({ data: [] })),
        api.get('/onboarding/isp-trainers').catch(() => ({ data: { trainers: [] } })),
      ]);
      setData(ov.data);
      const all = (emp.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setStaff(all.filter(e => e.is_active !== false));
      setFormerStaff(all.filter(e => e.is_active === false));
      setIsp(ispRes.data || []);
      setParticipants((parts.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setTrainers(trs.data?.trainers || []);
    } catch { toast.error('Failed to load annual training data'); }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const activeModules = useMemo(() => (data?.modules || []).filter(m => m.is_active !== false), [data]);
  const visibleStaff = useMemo(() => {
    const s = staffSearch.trim().toLowerCase();
    return s ? staff.filter(x => (x.name || '').toLowerCase().includes(s)) : staff;
  }, [staff, staffSearch]);

  const toggleStaff = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const assign = async () => {
    if (!moduleId || !dueDate || selected.length === 0) { toast.error('Pick a training, at least one staffer, and a due date'); return; }
    setAssigning(true);
    try {
      const { data: res } = await api.post(`/onboarding/modules/${moduleId}/assign`, {
        employee_ids: selected, due_date: dueDate, note, reset_progress: resetProgress,
      });
      toast.success(`Assigned "${res.module_title}" to ${res.assigned} staff — due ${res.due_date}`);
      setSelected([]); setNote(''); setResetProgress(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    setAssigning(false);
  };

  const downloadCert = async (row) => {
    try {
      const resp = await api.get(`/onboarding/annual-certificate/${row.employee_id}/${row.module_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(row.employee_name || 'staff').replace(/[^A-Za-z0-9]+/g, '_')}_${(row.module_title || 'training').replace(/[^A-Za-z0-9]+/g, '_')}_certificate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Certificate not available'); }
  };

  const cancelAssignment = async (row) => {
    if (!window.confirm(`Cancel "${row.module_title}" for ${row.employee_name}?`)) return;
    try {
      await api.delete(`/onboarding/assignments/${row.employee_id}/${row.module_id}`);
      toast.success('Assignment removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleIspStaff = (id) =>
    setIspForm(f => ({ ...f, employee_ids: f.employee_ids.includes(id) ? f.employee_ids.filter(x => x !== id) : [...f.employee_ids, id] }));

  const addTrainer = async () => {
    const name = window.prompt('New trainer name:');
    if (!name?.trim()) return;
    try {
      const { data } = await api.post('/onboarding/isp-trainers', { name: name.trim() });
      setTrainers(data.trainers);
      setIspForm(f => ({ ...f, trainer_name: name.trim() }));
      toast.success(`${name.trim()} added to trainers`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add trainer'); }
  };

  const blankIspForm = { employee_ids: [], date: '', hours: '', participant: '', participantOther: '', notes: '', trainer_name: '', program: '' };

  const startEditIsp = (t) => {
    const known = participants.some(p => p.name === t.participant);
    setEditingIsp(t);
    setIspForm({
      employee_ids: [t.employee_id],
      date: t.date, hours: String(t.hours),
      participant: known ? t.participant : '__other',
      participantOther: known ? '' : t.participant,
      notes: t.notes || '', trainer_name: t.trainer_name || '', program: t.program || '',
    });
    document.querySelector('[data-testid="isp-card"]')?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelEditIsp = () => { setEditingIsp(null); setIspForm(blankIspForm); setIspSearch(''); };

  const saveIsp = async () => {
    const f = ispForm;
    const participant = f.participant === '__other' ? f.participantOther.trim() : f.participant;
    if (editingIsp) {
      if (!f.date || !f.hours || !participant) { toast.error('Date, hours, and participant are required'); return; }
      setIspSaving(true);
      try {
        await api.put(`/onboarding/isp-trainings/${editingIsp.id}`, {
          date: f.date, hours: parseFloat(f.hours), participant,
          notes: f.notes, trainer_name: f.trainer_name, program: f.program,
        });
        toast.success('Training updated');
        cancelEditIsp();
        load();
      } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
      setIspSaving(false);
      return;
    }
    if (!f.employee_ids.length || !f.date || !f.hours || !participant) {
      toast.error('Pick at least one staff member, date, hours, and participant');
      return;
    }
    setIspSaving(true);
    try {
      const { data: res } = await api.post('/onboarding/isp-trainings', {
        employee_ids: f.employee_ids, date: f.date, hours: parseFloat(f.hours),
        participant, notes: f.notes, trainer_name: f.trainer_name, program: f.program,
      });
      toast.success(`ISP training recorded for ${res.created} staff member${res.created === 1 ? '' : 's'}`);
      setIspForm(blankIspForm);
      setIspSearch('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setIspSaving(false);
  };

  const deleteIsp = async (t) => {
    if (!window.confirm(`Delete ISP training for ${t.employee_name} on ${t.date}?`)) return;
    try { await api.delete(`/onboarding/isp-trainings/${t.id}`); load(); } catch { toast.error('Delete failed'); }
  };

  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--accent)]" /></div>;

  const rows = data.rows || [];
  const inputCls = 'w-full px-3 py-2 border border-[var(--rule)] rounded text-sm bg-white';

  return (
    <div className="space-y-8" data-testid="annual-training-tab">
      {/* Assign */}
      <div className="bg-white border border-[var(--rule)] rounded-xl p-5 shadow-sm" data-testid="annual-assign-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock className="h-5 w-5 text-[var(--accent)]" />
          <h2 className="display text-lg">Assign an annual training</h2>
        </div>
        {activeModules.length === 0 ? (
          <p className="text-sm text-[var(--ink-fade)]">No annual modules yet — create one in the Module catalog and set its category to <b>Annual training</b>.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div>
                <label className="small-caps text-[var(--ink-fade)] block mb-1">Training</label>
                <select className={inputCls} value={moduleId} onChange={e => setModuleId(e.target.value)} data-testid="annual-module-select">
                  <option value="">— pick a training —</option>
                  {activeModules.map(m => <option key={m.id} value={m.id}>{`${m.title} (${m.hours}h)`}</option>)}
                </select>
              </div>
              <div>
                <label className="small-caps text-[var(--ink-fade)] block mb-1">Due date</label>
                <input type="date" className={inputCls} value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="annual-due-date" />
              </div>
              <div>
                <label className="small-caps text-[var(--ink-fade)] block mb-1">Note (optional)</label>
                <input className={inputCls} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Complete before your annual review" data-testid="annual-note" />
              </div>
              <label className="flex items-center gap-2 text-xs text-[var(--ink-soft)] cursor-pointer">
                <input type="checkbox" checked={resetProgress} onChange={e => setResetProgress(e.target.checked)} data-testid="annual-reset" />
                Re-certification — wipe prior completion so staff must re-take
              </label>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="small-caps text-[var(--ink-fade)]">Staff ({selected.length} selected)</label>
                <input className="px-2 py-1 border border-[var(--rule)] rounded text-xs w-44" placeholder="Search staff…"
                       value={staffSearch} onChange={e => setStaffSearch(e.target.value)} data-testid="annual-staff-search" />
              </div>
              <div className="border border-[var(--rule)] rounded-lg max-h-52 overflow-y-auto divide-y divide-[var(--rule)]" data-testid="annual-staff-list">
                {visibleStaff.map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#F8F5FB] cursor-pointer">
                    <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleStaff(s.id)} data-testid={`annual-staff-${s.id}`} />
                    <span className="flex-1">{s.name}</span>
                    <span className="text-[10px] text-[var(--ink-fade)]">{s.role}</span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end mt-3">
                <button onClick={assign} disabled={assigning} className="btn-primary flex items-center gap-1.5" data-testid="annual-assign-btn">
                  {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Assign training
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tracking grid */}
      <div className="bg-white border border-[var(--rule)] rounded-xl shadow-sm overflow-hidden" data-testid="annual-tracking-card">
        <div className="px-5 py-3 border-b border-[var(--rule)] flex items-center gap-2">
          <Award className="h-5 w-5 text-[var(--accent)]" />
          <h2 className="display text-lg">Tracking</h2>
          <span className="text-xs text-[var(--ink-fade)] ml-auto">{rows.length} assignment{rows.length === 1 ? '' : 's'}</span>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-[var(--ink-fade)] text-center py-8">No annual trainings assigned yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-fade)] bg-[#F8F5FB]">
                <th className="px-4 py-2">Staff</th><th className="px-4 py-2">Training</th><th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Due</th><th className="px-4 py-2">Completed</th><th className="px-4 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {rows.map(r => {
                  const meta = STATUS_META[r.status] || STATUS_META.assigned;
                  return (
                    <tr key={`${r.employee_id}-${r.module_id}`} data-testid={`annual-row-${r.employee_id}-${r.module_id}`}>
                      <td className="px-4 py-2 font-medium">{r.employee_name}</td>
                      <td className="px-4 py-2">{r.module_title} <span className="text-[10px] text-[var(--ink-fade)]">({r.hours}h)</span></td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.overdue ? 'bg-[#FFEBEE] text-[#B71C1C]' : meta.cls}`}>
                          {r.overdue ? 'OVERDUE' : meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 tabular-nums">{r.due_date || '—'}</td>
                      <td className="px-4 py-2 tabular-nums">{r.completed_at ? r.completed_at.slice(0, 10) : '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {r.status === 'approved' ? (
                          <button onClick={() => downloadCert(r)} className="text-[var(--accent)] hover:underline text-xs inline-flex items-center gap-1"
                                  data-testid={`annual-cert-${r.employee_id}-${r.module_id}`}>
                            <Download className="h-3.5 w-3.5" /> Certificate
                          </button>
                        ) : (
                          <button onClick={() => cancelAssignment(r)} className="text-[var(--ink-fade)] hover:text-[#B71C1C] text-xs inline-flex items-center gap-1"
                                  data-testid={`annual-cancel-${r.employee_id}-${r.module_id}`}>
                            <Trash2 className="h-3.5 w-3.5" /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ISP trainings */}
      <div className="bg-white border border-[var(--rule)] rounded-xl shadow-sm" data-testid="isp-card">
        <div className="px-5 py-3 border-b border-[var(--rule)] flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-[var(--accent)]" />
          <div>
            <h2 className="display text-lg">ISP Trainings</h2>
            <p className="text-[11px] text-[var(--ink-fade)]">Trainings done outside the system with a supervisor or manager — recorded for the staff transcript.</p>
          </div>
        </div>
        <div className="p-5 border-b border-[var(--rule)] space-y-3">
          {editingIsp && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-[#FFF3E0] text-[#B45309] text-xs" data-testid="isp-edit-banner">
              <Pencil className="h-3.5 w-3.5" />
              Correcting the {editingIsp.date} training for <b>{editingIsp.employee_name}</b>
              <button onClick={cancelEditIsp} className="ml-auto inline-flex items-center gap-1 hover:underline" data-testid="isp-edit-cancel">
                <XIcon className="h-3 w-3" /> Cancel
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Multi-staff picker — includes former staff so old trainings can be recorded */}
            <div className={editingIsp ? 'opacity-50 pointer-events-none' : ''}>
              <div className="flex items-center justify-between mb-1">
                <label className="small-caps text-[var(--ink-fade)]">Staff members ({ispForm.employee_ids.length} selected)</label>
                <input className="px-2 py-1 border border-[var(--rule)] rounded text-xs w-40" placeholder="Search…"
                       value={ispSearch} onChange={e => setIspSearch(e.target.value)} data-testid="isp-staff-search" />
              </div>
              <div className="border border-[var(--rule)] rounded-lg max-h-44 overflow-y-auto divide-y divide-[var(--rule)]" data-testid="isp-staff-list">
                {staff.filter(s => !ispSearch || (s.name || '').toLowerCase().includes(ispSearch.toLowerCase())).map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#F8F5FB] cursor-pointer">
                    <input type="checkbox" checked={ispForm.employee_ids.includes(s.id)} onChange={() => toggleIspStaff(s.id)} data-testid={`isp-staff-${s.id}`} />
                    <span className="flex-1">{s.name}</span>
                  </label>
                ))}
                {formerStaff.filter(s => !ispSearch || (s.name || '').toLowerCase().includes(ispSearch.toLowerCase())).length > 0 && (
                  <div className="px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-[var(--ink-fade)] bg-[#F8F5FB]" data-testid="isp-former-header">Former staff (archived)</div>
                )}
                {formerStaff.filter(s => !ispSearch || (s.name || '').toLowerCase().includes(ispSearch.toLowerCase())).map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[#F8F5FB] cursor-pointer opacity-75">
                    <input type="checkbox" checked={ispForm.employee_ids.includes(s.id)} onChange={() => toggleIspStaff(s.id)} data-testid={`isp-staff-${s.id}`} />
                    <span className="flex-1">{s.name} <span className="text-[10px] text-[var(--ink-fade)]">(former)</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 content-start">
              <input type="date" className={inputCls} value={ispForm.date} onChange={e => setIspForm(f => ({ ...f, date: e.target.value }))} data-testid="isp-date" />
              <input type="number" step="0.25" min="0" className={inputCls} placeholder="Hours" value={ispForm.hours} onChange={e => setIspForm(f => ({ ...f, hours: e.target.value }))} data-testid="isp-hours" />
              <div className="flex gap-1">
                <select className={`${inputCls} flex-1`} value={ispForm.trainer_name} onChange={e => setIspForm(f => ({ ...f, trainer_name: e.target.value }))} data-testid="isp-trainer-select">
                  <option value="">Trainer…</option>
                  {trainers.map(t => <option key={t} value={t}>{t}</option>)}
                  {ispForm.trainer_name && !trainers.includes(ispForm.trainer_name) && (
                    <option value={ispForm.trainer_name}>{ispForm.trainer_name}</option>
                  )}
                </select>
                <button onClick={addTrainer} title="Add a new trainer"
                        className="px-2 border border-[var(--rule)] rounded text-[var(--accent)] hover:border-[var(--accent)] bg-white"
                        data-testid="isp-trainer-add">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <select className={inputCls} value={ispForm.participant} onChange={e => setIspForm(f => ({ ...f, participant: e.target.value }))} data-testid="isp-participant-select">
                <option value="">Participant…</option>
                {participants.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                <option value="__other">Other…</option>
              </select>
              {ispForm.participant === '__other' && (
                <input className={`${inputCls} col-span-2`} placeholder="Type participant name" value={ispForm.participantOther}
                       onChange={e => setIspForm(f => ({ ...f, participantOther: e.target.value }))} data-testid="isp-participant-other" />
              )}
              <select className={inputCls} value={ispForm.program} onChange={e => setIspForm(f => ({ ...f, program: e.target.value }))} data-testid="isp-program-select">
                <option value="">Program…</option>
                {ISP_PROGRAMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input className={inputCls} placeholder="Notes" value={ispForm.notes} onChange={e => setIspForm(f => ({ ...f, notes: e.target.value }))} data-testid="isp-notes" />
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={saveIsp} disabled={ispSaving} className="btn-primary flex items-center gap-1.5" data-testid="isp-save-btn">
              {ispSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingIsp ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editingIsp ? 'Update training' : 'Record ISP training'}
            </button>
          </div>
        </div>
        {isp.length === 0 ? (
          <p className="text-sm text-[var(--ink-fade)] text-center py-6">No ISP trainings recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-fade)] bg-[#F8F5FB]">
                <th className="px-4 py-2">Staff</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Hours</th>
                <th className="px-4 py-2">Participant</th><th className="px-4 py-2">Program</th><th className="px-4 py-2">Trainer</th><th className="px-4 py-2">Notes</th><th className="px-2 py-2" />
              </tr></thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {isp.map(t => (
                  <tr key={t.id} className={editingIsp?.id === t.id ? 'bg-[#FFF8EC]' : ''} data-testid={`isp-row-${t.id}`}>
                    <td className="px-4 py-2 font-medium">{t.employee_name}</td>
                    <td className="px-4 py-2 tabular-nums">{t.date}</td>
                    <td className="px-4 py-2 tabular-nums">{t.hours}</td>
                    <td className="px-4 py-2">{t.participant}</td>
                    <td className="px-4 py-2">
                      {t.program ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F0EDF6] text-[#5A4D6E]">{t.program}</span> : '—'}
                    </td>
                    <td className="px-4 py-2">{t.trainer_name}</td>
                    <td className="px-4 py-2 text-[var(--ink-soft)] max-w-xs truncate" title={t.notes}>{t.notes || '—'}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button onClick={() => startEditIsp(t)} className="text-[var(--ink-fade)] hover:text-[var(--accent)] mr-2" title="Edit / correct" data-testid={`isp-edit-${t.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteIsp(t)} className="text-[var(--ink-fade)] hover:text-[#B71C1C]" data-testid={`isp-delete-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
